// The web's mirror of SuggestTenders. See pricing.ts for why these exist twice.

import { MAX_MINOR } from './pricing';

export const MIN_TENDER_SUGGESTIONS = 3;

// The largest total that stays exact, matching the server's ceiling.
const MAX_LINES = 200;
const MAX_MODIFIERS = 20;
const MAX_QTY_FOR_TOTAL = 999;
const MAX_TOTAL_WITH_FEE =
	MAX_LINES * MAX_QTY_FOR_TOTAL * (MAX_MINOR + MAX_MODIFIERS * MAX_MINOR) + MAX_MINOR;

/**
 * The amounts a cashier is actually handed.
 *
 * For each step, the smallest multiple strictly above the total; deduplicated
 * and sorted. Strictly above, because an amount equal to the total is the
 * "Exact" button the panel already has.
 *
 * When the steps yield fewer than three, it carries on with multiples of the
 * largest step, so a till with one step configured still fills its buttons.
 */
export function suggestTenders(totalMinor: number, steps: number[]): number[] {
	const seen = new Set<number>();
	const out: number[] = [];

	const add = (value: number) => {
		if (value <= totalMinor || value > MAX_TOTAL_WITH_FEE || seen.has(value)) return;
		seen.add(value);
		out.push(value);
	};

	let largest = 0;
	for (const step of steps) {
		if (step <= 0) continue;
		if (step > largest) largest = step;
		add(nextMultipleAbove(totalMinor, step));
	}

	while (largest > 0 && out.length < MIN_TENDER_SUGGESTIONS) {
		const highest = out.reduce((max, v) => (v > max ? v : max), totalMinor);
		const next = nextMultipleAbove(highest, largest);
		if (next <= highest) break;
		add(next);
	}

	return out.sort((a, b) => a - b);
}

function nextMultipleAbove(total: number, step: number): number {
	if (step <= 0) return 0;
	return (Math.floor(total / step) + 1) * step;
}
